import { ToWords } from 'to-words';

/**
 * Spoken-number normalization for voice (TTS) output.
 *
 * WHY THIS EXISTS: in voice mode the LLM emits digits ("$123,456.99", "45%")
 * because the digit form overwhelmingly dominates its training distribution and
 * because the tool results in its context show it digits — a system-prompt rule
 * asking it to "spell numbers as words" is the weakest possible lever against
 * that and is violated routinely, worst of all on LARGE numbers (exactly where
 * ElevenLabs TTS garbles them). ElevenLabs' own server-side normalization is not
 * supported on the Turbo v2.5 model we run, so the only reliable fix is to
 * convert the digits to words ourselves, deterministically, before the text
 * reaches TTS.
 *
 * SCOPE — we convert ONLY the patterns TTS actually breaks on AND that can't be
 * confused with something that should stay as digits:
 *   - `$`-prefixed currency        →  "$123,456.99" → "one hundred twenty three
 *                                      thousand four hundred fifty six dollars
 *                                      and ninety nine cents"
 *   - percentages                  →  "45%" → "forty five percent"
 *   - comma-GROUPED plain numbers  →  "1,234,567" → words. The comma grouping is
 *                                      the safety signal: it's a strong "this is
 *                                      an amount" marker. Dates, times, phone
 *                                      numbers, zip codes and record IDs never
 *                                      use thousands separators, so we never
 *                                      touch them.
 *
 * We deliberately LEAVE ALONE bare separator-less digit runs (years like 2026,
 * order IDs, zips), dates ("6/3"), times ("3:30"), hyphenated IDs ("Q-451") and
 * short numbers — TTS reads those acceptably, and converting them risks turning
 * a year or an ID into a mangled cardinal, which is worse than the status quo.
 *
 * Uses the same `to-words` instance config as the tool-output voice formatters
 * (apps/crm-api .../tool-execution.service.ts) so wording is identical whether a
 * number arrives via a tool result or is generated inline by the LLM.
 */
const toWords = new ToWords({
	localeCode: 'en-US',
	converterOptions: { doNotAddOnly: true },
});

/** Strip thousands separators and parse. Returns NaN on garbage. */
function parseGrouped(raw: string): number {
	return Number(raw.replace(/,/g, ''));
}

// A number core: either thousands-grouped (commas only BETWEEN digit triples, so
// a list-separator comma like "$18,000, $22,000" is never absorbed) or a plain
// digit run, each with an optional decimal.
const NUMBER_CORE = '(?:\\d{1,3}(?:,\\d{3})+(?:\\.\\d+)?|\\d+(?:\\.\\d+)?)';
// `$` then optional space, then the number core.
const CURRENCY_RE = new RegExp(`\\$\\s?(${NUMBER_CORE})`, 'g');
// The number core immediately before a percent sign.
const PERCENT_RE = new RegExp(`(${NUMBER_CORE})\\s?%`, 'g');
// A plain number that MUST carry at least one thousands group (e.g. "1,234").
// Lookbehind avoids re-touching a currency amount ($ already handled) or a token
// glued to a word/ID char; lookahead avoids "1,234th" / a trailing percent.
const GROUPED_RE = /(?<![\w$.,])(\d{1,3}(?:,\d{3})+(?:\.\d+)?)(?![\w%])/g;

/**
 * Convert the in-scope number patterns in a COMPLETE string to spoken words.
 * Pure and idempotent-ish on already-worded text (no digits left to match).
 *
 * Order matters: currency first (consumes the `$…` digits), then percent, then
 * bare grouped numbers — so a grouped amount is only ever converted once.
 */
export function convertSpokenNumbers(text: string): string {
	if (text === '') {
		return text;
	}

	let out = text.replace(CURRENCY_RE, (match, digits: string) => {
		const n = parseGrouped(digits);
		return isNaN(n)
			? match
			: toWords.convert(n, { currency: true }).toLowerCase();
	});

	out = out.replace(PERCENT_RE, (match, digits: string) => {
		const n = parseGrouped(digits);
		return isNaN(n) ? match : `${toWords.convert(n).toLowerCase()} percent`;
	});

	out = out.replace(GROUPED_RE, (match, digits: string) => {
		const n = parseGrouped(digits);
		return isNaN(n) ? match : toWords.convert(n).toLowerCase();
	});

	return out;
}

/**
 * Streaming-safe wrapper around {@link convertSpokenNumbers}. The LLM→TTS path
 * forwards token deltas as they arrive, so a single number can be split across
 * deltas in any of these ways:
 *
 *   "$"            + "123,456.99"
 *   "$123,"        + "456.99"
 *   "$123,456."    + "99"
 *
 * Converting per-delta would mangle the halves. This normalizer mirrors the
 * framework-tag carry buffer in conversation-manager: on each `push`, it holds
 * back the trailing run that could still be GROWING into a number (an optional
 * `$`, then digits with grouping/decimal punctuation, at end-of-string — or a
 * lone trailing `$`), converts and returns everything before it, and re-glues
 * the held fragment to the next delta. `flush()` converts and returns whatever
 * remains when the stream ends.
 *
 * Only the trailing partial number is ever withheld, so prose flushes
 * immediately — the only latency is a number sitting at the very end of the
 * whole response, which waits for the next delta or the final flush.
 */
// The leading `(?<![\d.,])` anchors the held run to a NUMBER-START boundary so
// it can never begin in the middle of an already-complete number. Without it,
// a terminator like a comma after a finished decimal ("$22.99,") let the regex
// match a mid-number sub-span ("99,") — splitting the number and converting the
// partial "$22." → "twenty two dollars" with a dangling ".99". The complete
// number must instead fall through to batch conversion.
const TRAILING_PARTIAL_NUMBER = /(?<![\d.,])(?:\$\s?)?\d[\d,]*\.?\d*$|\$$/;

export interface SpokenNumberNormalizer {
	push(text: string): string;
	flush(): string;
}

export function createSpokenNumberNormalizer(): SpokenNumberNormalizer {
	let carry = '';

	return {
		push(text: string): string {
			const working = carry + text;
			carry = '';
			const match = working.match(TRAILING_PARTIAL_NUMBER);
			if (match != null && match.index != null) {
				carry = working.slice(match.index);
				return convertSpokenNumbers(working.slice(0, match.index));
			}
			return convertSpokenNumbers(working);
		},
		flush(): string {
			const out = convertSpokenNumbers(carry);
			carry = '';
			return out;
		},
	};
}
